// Entra app roles. Every admin API route checks these on the server from the validated token's
// `roles` claim; hiding a page in the web app is never the control.

export const Role = {
  Admin: "HR.Admin",
  Agent: "HR.Agent",
} as const;

export type RoleName = (typeof Role)[keyof typeof Role];

// HR.Admin can do everything HR.Agent can.
const IMPLIES: Record<RoleName, RoleName[]> = {
  [Role.Admin]: [Role.Admin, Role.Agent],
  [Role.Agent]: [Role.Agent],
};

// API Gateway's JWT authorizer hands array claims to Lambda flattened into one bracketed string,
// "[HR.Admin HR.Agent]"; MSAL in the browser sees a real array. Accept both, nothing else.
function rawRoles(roles: unknown): unknown[] {
  if (Array.isArray(roles)) return roles;
  const m = typeof roles === "string" ? /^\[(.*)\]$/.exec(roles) : null;
  return m?.[1] ? m[1].split(/[\s,]+/).filter(Boolean) : [];
}

export function rolesFromClaims(claims: { roles?: unknown }): RoleName[] {
  const raw = rawRoles(claims.roles);
  const known = raw.filter((r): r is RoleName => r === Role.Admin || r === Role.Agent);
  return [...new Set(known.flatMap((r) => IMPLIES[r]))];
}

export function hasRole(claims: { roles?: unknown }, role: RoleName): boolean {
  return rolesFromClaims(claims).includes(role);
}

export class ForbiddenError extends Error {
  readonly status = 403;
  constructor(role: RoleName) {
    super(`requires ${role}`);
    this.name = "ForbiddenError";
  }
}

export function requireRole(claims: { roles?: unknown }, role: RoleName): void {
  if (!hasRole(claims, role)) throw new ForbiddenError(role);
}
